import React, { useCallback, useMemo, useState } from 'react';
import { Col, Row, Tooltip, message } from 'antd-v5';
import dayjs from 'dayjs';
import PageHeaderLayout from '@/component/page-header-layout';
import { Button, Card, DatePicker, Form, Input, Modal, Select, Table } from '@/components/index';
import { getLargeModelLogPageRequest } from '@/state/request/doubao';

import './index.less';

const DEFAULT_PAGE = { pageNo: 1, pageSize: 10 };
const MODEL_OPTIONS = [
  { label: '语音识别(智能模式)', value: 'V870语音识别' },
  { label: '语音识别(极速模式)', value: '极速开单' },
  { label: '图片识别', value: 'V870图片识别' },
  { label: 'V910语音识别', value: 'V910语音识别' },
  { label: 'V910图片识别', value: 'V910图片识别' },
];
const MODEL_LABELS = MODEL_OPTIONS.reduce((result, item) => {
  result[item.value] = item.label;
  return result;
}, {});
const CORRECTED_INPUT_MODEL_VERSIONS = new Set(['V870语音识别', 'V910语音识别']);

const renderValue = (value) => (value === null || value === undefined || value === '' ? '--' : value);
const hasOriginalInput = (record) => Boolean(record?.originalInputContent);
const hasCorrectedInput = (record) =>
  CORRECTED_INPUT_MODEL_VERSIONS.has(record?.modelVersion) && hasOriginalInput(record);

const LargeModelLogPage = () => {
  const [form] = Form.useForm();
  const [query, setQuery] = useState(DEFAULT_PAGE);
  const [pageData, setPageData] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const loadData = useCallback(async (params) => {
    setLoading(true);
    try {
      const response = await getLargeModelLogPageRequest(params);
      if (response?.code === 200) {
        const data = response.data || {};
        setPageData({
          ...data,
          pageList: (data.pageList || []).map((item, index) => ({
            ...item,
            rowKey: `${item.logTime}-${item.stallId}-${index}`,
          })),
        });
      } else {
        setPageData({});
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = (values) => {
    const businessCode = values.businessCode?.trim();
    if (!values.stallId && !businessCode) {
      message.warning('请输入商行ID或商行业务ID');
      return;
    }
    const nextQuery = {
      ...DEFAULT_PAGE,
      stallId: values.stallId ? Number(values.stallId) : undefined,
      businessCode: businessCode || undefined,
      modelVersion: values.modelVersion,
      startTime: values.timeRange[0].valueOf(),
      endTime: values.timeRange[1].valueOf(),
    };
    setQuery(nextQuery);
    loadData(nextQuery);
  };

  const handleReset = () => {
    form.resetFields();
    setQuery(DEFAULT_PAGE);
    setPageData({});
    setSelectedRecord(null);
  };

  const productColumns = useMemo(
    () => [
      { title: '货品名称', dataIndex: 'productName', width: 150, render: renderValue },
      { title: '数量', dataIndex: 'saleNumber', width: 90, render: renderValue },
      { title: '数量单位', dataIndex: 'numberUnit', width: 100, render: renderValue },
      { title: '重量', dataIndex: 'saleWeight', width: 90, render: renderValue },
      { title: '重量单位', dataIndex: 'weightUnit', width: 100, render: renderValue },
      { title: '单价', dataIndex: 'price', width: 90, render: renderValue },
      { title: '单价单位', dataIndex: 'priceUnit', width: 100, render: renderValue },
      { title: '去皮', dataIndex: 'iceWeight', width: 90, render: renderValue },
      { title: 'Sale字段', dataIndex: 'sale', width: 120, render: renderValue },
    ],
    [],
  );

  const columns = useMemo(
    () => [
      {
        title: '调用时间',
        dataIndex: 'logTime',
        width: 180,
        render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '--'),
      },
      { title: '商行ID', dataIndex: 'stallId', width: 110 },
      {
        title: '识别类型',
        dataIndex: 'modelVersion',
        width: 140,
        render: (value) => MODEL_LABELS[value] || value || '--',
      },
      {
        title: '输入内容摘要',
        dataIndex: 'inputContent',
        width: '28%',
        render: (value, record) => {
          const displayValue = hasOriginalInput(record) ? record.originalInputContent : value;
          return (
            <Tooltip title={displayValue} placement='topLeft'>
              <span className='large-model-log-input'>{displayValue || '--'}</span>
            </Tooltip>
          );
        },
      },
      {
        title: '输出结果',
        dataIndex: 'products',
        width: 130,
        align: 'center',
        render: (products = []) => (
          <span className='large-model-log-count'>{products.length} 条数据</span>
        ),
      },
      {
        title: '操作',
        key: 'action',
        width: 100,
        align: 'center',
        render: (_, record) => (
          <button
            type='button'
            className='large-model-log-detail-button'
            onClick={() => setSelectedRecord(record)}
          >
            查看详情
          </button>
        ),
      },
    ],
    [],
  );

  const handleTableChange = (pagination) => {
    if (!query.stallId && !query.businessCode) {
      message.warning('请先输入商行ID或商行业务ID');
      return;
    }
    const nextQuery = {
      ...query,
      pageNo: pagination.current || 1,
      pageSize: pagination.pageSize || query.pageSize,
    };
    setQuery(nextQuery);
    loadData(nextQuery);
  };

  return (
    <PageHeaderLayout>
      <div className='large-model-log-container'>
        <Card bordered={false} bodyStyle={{ padding: '0 16px 16px' }}>
          <Form form={form} layout='inline' onFinish={handleSearch}>
            <Row gutter={16} style={{ width: '100%' }}>
              <Col xs={24} md={12} xl={5} style={{ marginTop: 16 }}>
                <Form.Item
                  label='商行ID'
                  name='stallId'
                  rules={[
                    { pattern: /^[1-9]\d*$/, message: '商行ID只能输入正整数' },
                  ]}
                >
                  <Input allowClear placeholder='请输入商行ID' maxLength={10} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={5} style={{ marginTop: 16 }}>
                <Form.Item label='商行业务ID' name='businessCode'>
                  <Input allowClear placeholder='请输入商行业务ID' maxLength={30} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={5} style={{ marginTop: 16 }}>
                <Form.Item
                  label='识别类型'
                  name='modelVersion'
                  rules={[{ required: true, message: '请选择识别类型' }]}
                >
                  <Select options={MODEL_OPTIONS} placeholder='请选择识别类型' />
                </Form.Item>
              </Col>
              <Col xs={24} xl={9} style={{ marginTop: 16 }}>
                <Form.Item
                  label='查询时间'
                  name='timeRange'
                  rules={[{ required: true, message: '请选择查询时间范围' }]}
                >
                  <DatePicker.RangePicker
                    showTime
                    style={{ width: '100%' }}
                    format='YYYY-MM-DD HH:mm:ss'
                    placeholder={['开始时间', '结束时间']}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} style={{ marginTop: 16 }}>
                <div className='large-model-log-toolbar'>
                  <Button type='primary' htmlType='submit'>查询</Button>
                  <Button onClick={handleReset}>重置</Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card>

        <Card bordered={false} style={{ marginTop: 8 }}>
          <Table
            rowKey='rowKey'
            tableLayout='fixed'
            loading={loading}
            columns={columns}
            dataSource={pageData.pageList || []}
            pagination={{
              current: pageData.pageNo || query.pageNo,
              pageSize: pageData.pageSize || query.pageSize,
              total: pageData.totalSize || 0,
              showSizeChanger: true,
            }}
            onChange={handleTableChange}
          />
        </Card>

        <Modal
          rootClassName='large-model-log-modal'
          title='快速开单日志详情'
          open={Boolean(selectedRecord)}
          onCancel={() => setSelectedRecord(null)}
          width={1200}
          destroyOnClose
          footer={
            <Button onClick={() => setSelectedRecord(null)}>
              关闭
            </Button>
          }
        >
          {selectedRecord && (
            <div className='large-model-log-detail'>
              <div className='large-model-log-meta'>
                <div className='large-model-log-meta-item'>
                  <span className='large-model-log-meta-label'>调用时间</span>
                  <span className='large-model-log-meta-value'>
                    {selectedRecord.logTime
                      ? dayjs(selectedRecord.logTime).format('YYYY-MM-DD HH:mm:ss')
                      : '--'}
                  </span>
                </div>
                <div className='large-model-log-meta-item'>
                  <span className='large-model-log-meta-label'>商行ID</span>
                  <span className='large-model-log-meta-value'>
                    {selectedRecord.stallId || '--'}
                  </span>
                </div>
                <div className='large-model-log-meta-item'>
                  <span className='large-model-log-meta-label'>识别类型</span>
                  <span className='large-model-log-meta-value large-model-log-model-name'>
                    {MODEL_LABELS[selectedRecord.modelVersion] || selectedRecord.modelVersion || '--'}
                  </span>
                </div>
              </div>
              <section className='large-model-log-detail-section'>
                <div className='large-model-log-section-header'>
                  <div>
                    <div className='large-model-log-section-title'>
                      {hasOriginalInput(selectedRecord) ? '原始输入内容' : '输入内容'}
                      <span className='large-model-log-type'>文本</span>
                    </div>
                    <div className='large-model-log-section-description'>
                      {hasOriginalInput(selectedRecord)
                        ? '货品名称纠正前的原始识别文本'
                        : '本次提交给大模型的内容'}
                    </div>
                  </div>
                </div>
                <div className='large-model-log-input-content'>
                  {(hasOriginalInput(selectedRecord)
                    ? selectedRecord.originalInputContent
                    : selectedRecord.inputContent) || '--'}
                </div>
              </section>
              {hasCorrectedInput(selectedRecord) && (
                <section className='large-model-log-detail-section'>
                  <div className='large-model-log-section-header'>
                    <div>
                      <div className='large-model-log-section-title'>
                        纠正后输入内容
                        <span className='large-model-log-type'>文本</span>
                      </div>
                      <div className='large-model-log-section-description'>实际提交给大模型的文本</div>
                    </div>
                  </div>
                  <div className='large-model-log-input-content'>
                    {selectedRecord.inputContent || '--'}
                  </div>
                </section>
              )}
              <section className='large-model-log-detail-section'>
                <div className='large-model-log-section-header'>
                  <div>
                    <div className='large-model-log-section-title'>
                      输出内容
                      <span className='large-model-log-type'>列表</span>
                    </div>
                    <div className='large-model-log-section-description'>大模型识别出的货品明细</div>
                  </div>
                  <span className='large-model-log-result-count'>
                    共 {selectedRecord.products?.length || 0} 条
                  </span>
                </div>
                <div className='large-model-log-output-table'>
                  <Table
                    rowKey={(_, index) => index}
                    columns={productColumns}
                    dataSource={selectedRecord.products || []}
                    pagination={false}
                    scroll={{ x: 1040, y: 360 }}
                  />
                </div>
              </section>
            </div>
          )}
        </Modal>
      </div>
    </PageHeaderLayout>
  );
};

export default LargeModelLogPage;
